/**
 * ============================================================================
 * TEMPLATES STORE - UPDATE OPERATIONS
 * ============================================================================
 * 
 * Handles updating existing templates via the backend API.
 * 
 * Features:
 * - Optimistic updates for instant UI feedback
 * - Automatic rollback on failure
 * - Change severity detection (breaking/review)
 * 
 * @module templates/api/update
 */

import { apiService } from "@/lib/api-functions/api-service";
import { templatesStore } from "../store";
import type { GlossaryBlueprint } from "@/types-games";
import type { UpdateTemplateData, ToastFunction } from "../types";

// ============================================================================
// UPDATE OPERATIONS
// ============================================================================

/**
 * Update an existing template via API and sync the store.
 * 
 * Features:
 * - Performs optimistic update for instant UI feedback
 * - Automatically rolls back on API failure
 * - Detects change severity (breaking changes vs review needed)
 * 
 * @param template - The template data to update (must include id)
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to the updated template
 * 
 * @example
 * ```ts
 * const updated = await updateTemplate({
 *   id: 'template-123',
 *   game_id: 'game-456',
 *   name: 'Updated Template',
 *   // ... other fields
 * });
 * ```
 */
export async function updateTemplate(
  template: UpdateTemplateData,
  toast?: ToastFunction
): Promise<GlossaryBlueprint> {
  // Store the current state for potential rollback
  const currentData = templatesStore.getData();
  const existingIndex = currentData.findIndex(bp => bp.id === template.id);
  
  if (existingIndex === -1) {
    console.warn('[updateTemplate] Template not found in store');
  }
  
  // ========================================================================
  // OPTIMISTIC UPDATE
  // ========================================================================
  // Apply changes immediately for instant UI feedback
  if (existingIndex >= 0) {
    const optimisticTemplate = {
      ...currentData[existingIndex],
      ...template,
      data: template.data as unknown as Record<string, unknown>,
      updated_at: new Date().toISOString(),
    } as unknown as GlossaryBlueprint;
    
    const optimisticData = [...currentData];
    optimisticData[existingIndex] = optimisticTemplate;
    templatesStore.setData(optimisticData);
  }

  try {
    // Validate authentication
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // ======================================================================
    // SEND TO API
    // ======================================================================
    const result = await apiService.updateBlueprint(template);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update template');
    }

    const updatedTemplate = result.data;

    // ======================================================================
    // SYNC STORE WITH SERVER RESPONSE
    // ======================================================================
    const finalData = templatesStore.getData();
    const finalIndex = finalData.findIndex(bp => bp.id === template.id);
    
    if (finalIndex >= 0) {
      const newData = [...finalData];
      newData[finalIndex] = updatedTemplate;
      templatesStore.setData(newData);
    } else {
      console.warn('[updateTemplate] Template not found in store after API call, adding it');
      const newData = [...finalData, updatedTemplate];
      templatesStore.setData(newData);
    }

    // ======================================================================
    // HANDLE SEVERITY INFORMATION
    // ======================================================================
    const severity = (result as any).severity;
    if (severity && toast) {
      if (severity.level === 'breaking') {
        toast({
          title: "Breaking Changes Detected",
          description: `Template updated. ${severity.affectedCount} item${severity.affectedCount !== 1 ? 's' : ''} require manual review.`,
          variant: "default",
        });
      } else if (severity.level === 'review') {
        toast({
          title: "Template Updated",
          description: `${severity.affectedCount} item${severity.affectedCount !== 1 ? 's' : ''} can be bulk-updated. Check version history to update.`,
          variant: "default",
        });
      }
      
      console.log('[updateTemplate] Change severity:', severity);
    }

    console.log(`[updateTemplate] Updated template: ${updatedTemplate.id} - ${updatedTemplate.name}`);
    return updatedTemplate;
    
  } catch (err) {
    // ======================================================================
    // ROLLBACK ON FAILURE
    // ======================================================================
    templatesStore.setData(currentData);
    
    const message = err instanceof Error ? err.message : 'Failed to update template';
    console.error('[updateTemplate] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
